import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import {
  nailsInMotionVideos,
  type NailsInMotionVideo,
} from '@/content/nailsInMotionVideos'
import { MotionReelCard } from '@/components/gallery/MotionReelCard'
import { smoothEase } from '@/lib/motion'
import { cn } from '@/lib/utils'

function ChevronRightScrollHint({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10 8l4 4-4 4"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

type NailsInMotionStripProps = {
  /** Defaults to `nailsInMotionVideos` from content */
  videos?: readonly NailsInMotionVideo[]
  className?: string
}

/** Horizontal reel of vertical salon clips — shared by home and gallery pages. */
export function NailsInMotionStrip({ videos = nailsInMotionVideos, className }: NailsInMotionStripProps) {
  const reduced = useReducedMotion()
  const sectionRootRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  const [scrollFade, setScrollFade] = useState({ left: false, right: true })
  const [stripAudible, setStripAudible] = useState(true)
  const [audibleReelId, setAudibleReelId] = useState<string | null>(null)

  const handleAudibleChange = useCallback((id: string, audible: boolean) => {
    setAudibleReelId((prev) => (audible ? id : prev === id ? null : prev))
  }, [])

  useLayoutEffect(() => {
    const el = stripRef.current
    if (!el) return

    const update = () => {
      const cw = el.clientWidth
      const sw = el.scrollWidth
      const sl = el.scrollLeft
      const maxScroll = Math.max(0, sw - cw)
      const eps = 8
      const overflow = sw > cw + eps
      setScrollFade({
        left: overflow && sl > eps,
        right: overflow && sl < maxScroll - eps,
      })
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(update)
    })
    ro.observe(el)
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [videos.length])

  useEffect(() => {
    const el = sectionRootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e) return
        const on = e.isIntersecting && e.intersectionRatio >= 0.05
        setStripAudible(on)
      },
      { threshold: [0, 0.02, 0.05, 0.08, 0.12, 0.2, 0.35] }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [videos.length])

  if (videos.length === 0) return null

  return (
    <div ref={sectionRootRef} className={cn('relative', className)}>
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 z-[1] w-10 bg-linear-to-r from-canvas/50 to-transparent sm:hidden',
          'transition-opacity duration-300',
          scrollFade.left ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-[1] w-10 bg-linear-to-l from-canvas/50 to-transparent sm:hidden',
          'transition-opacity duration-300',
          scrollFade.right ? 'opacity-100' : 'opacity-0'
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute right-1 top-1/2 z-[1] -translate-y-1/2 sm:hidden',
          'transition-opacity duration-300 motion-reduce:transition-none',
          scrollFade.right ? 'opacity-70' : 'opacity-0'
        )}
        aria-hidden
      >
        <ChevronRightScrollHint className="size-6 text-ink/35 drop-shadow-sm" />
      </div>

      <div
        ref={stripRef}
        className={cn(
          'flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none]',
          '[&::-webkit-scrollbar]:hidden',
          'snap-x snap-mandatory sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:snap-none lg:grid-cols-3'
        )}
      >
        {videos.map((item, i) => (
          <motion.div
            key={item.id}
            className="w-[42vw] shrink-0 snap-center sm:w-auto"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-30px' }}
            transition={{
              duration: reduced ? 0 : 0.45,
              delay: reduced ? 0 : Math.min(i * 0.04, 0.2),
              ease: smoothEase,
            }}
          >
            <div
              className={cn(
                'relative overflow-hidden rounded-[var(--radius-button)] transition-shadow duration-300 ease-out motion-reduce:transition-none',
                !reduced &&
                  audibleReelId != null &&
                  audibleReelId !== item.id &&
                  'after:pointer-events-none after:absolute after:inset-0 after:z-[1] after:bg-black/55 after:transition-opacity after:duration-300 motion-reduce:after:transition-none',
                !reduced && audibleReelId === item.id && 'relative z-[2]'
              )}
            >
              <MotionReelCard
                reelId={item.id}
                src={item.src}
                poster={item.poster}
                description={item.description}
                reduced={!!reduced}
                stripAudible={stripAudible}
                onAudibleChange={handleAudibleChange}
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
